import { useTodaysInvestmentTip } from "@/hooks/useInvestmentTips";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, TrendingUp, DollarSign, Calendar, AlertTriangle, Target, BarChart3 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { TipContent } from "@/components/TipContent";
import { generateTLDR } from "@/utils/generateTLDR";

// Investment-related images mapping
const getInvestmentImage = (category?: string, id?: string): string => {
  const images = {
    stocks: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=300&fit=crop",
    etf: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&h=300&fit=crop",
    crypto: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=300&fit=crop",
    bonds: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop",
    real_estate: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop",
    commodities: "https://images.unsplash.com/photo-1582560475093-ba66ac2c6dd5?w=400&h=300&fit=crop",
    default: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=400&h=300&fit=crop"
  };

  if (category && category in images) {
    return images[category as keyof typeof images];
  }

  // Use ID to select a deterministic image if no category match
  if (id) {
    const imageKeys = Object.keys(images).filter(key => key !== 'default');
    const hash = id.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    const index = Math.abs(hash) % imageKeys.length;
    return images[imageKeys[index] as keyof typeof images];
  }

  return images.default;
};

const getRiskLevelColor = (riskLevel?: string) => {
  switch (riskLevel?.toLowerCase()) {
    case 'low':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
    case 'high':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
    case 'medium':
    default:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
  }
};

const formatCurrency = (amount?: number) => {
  if (!amount) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const calculatePotentialReturn = (current?: number, target?: number) => {
  if (!current || !target) return null;
  const returnPercent = ((target - current) / current) * 100;
  return returnPercent;
};

export const TodaysInvestmentTip = () => {
  const { data: investmentTip, isLoading, error } = useTodaysInvestmentTip();

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-5 w-20" />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="aspect-video w-full rounded-lg" />
          <Skeleton className="h-6 w-3/4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !investmentTip) {
    return (
      <Card className="w-full bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Today's Investment Tip
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">
              No investment tip available today
            </p>
            <p className="text-sm text-muted-foreground">
              Check back later for the latest market analysis and investment opportunities!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const imageUrl = investmentTip.image_url || getInvestmentImage(investmentTip.category, investmentTip.id);
  const potentialReturn = calculatePotentialReturn(investmentTip.current_price, investmentTip.target_price);

  return (
    <Card className="w-full bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm border-border/50 hover:shadow-glow-primary transition-all duration-300">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="h-5 w-5 text-primary" />
            Today's Investment Tip
          </CardTitle>
          <Badge variant="secondary" className="font-medium">
            {investmentTip.category.charAt(0).toUpperCase() + investmentTip.category.slice(1)}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Hero Image */}
        <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-border/20">
          <img
            src={imageUrl}
            alt={investmentTip.title}
            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
          />
          {investmentTip.ticker_symbol && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-black/70 text-white border-white/20 backdrop-blur-sm font-mono text-sm">
                {investmentTip.ticker_symbol}
              </Badge>
            </div>
          )}
        </div>
        
        {/* Title and Company */}
        <div className="space-y-2">
          <h3 className="text-2xl font-bold leading-tight bg-gradient-primary bg-clip-text text-transparent">
            {investmentTip.title}
          </h3>
          {investmentTip.company_name && (
            <p className="text-lg text-muted-foreground font-medium">
              {investmentTip.company_name}
            </p>
          )}
        </div>

        {/* Key Metrics */}
        {(investmentTip.current_price || investmentTip.target_price || investmentTip.risk_level) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {investmentTip.current_price && (
              <div className="bg-gradient-secondary rounded-xl p-4 border border-border/20">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Current Price</span>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(investmentTip.current_price)}</div>
              </div>
            )}
            
            {investmentTip.target_price && (
              <div className="bg-gradient-secondary rounded-xl p-4 border border-border/20">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Target Price</span>
                </div>
                <div className="text-2xl font-bold text-primary">{formatCurrency(investmentTip.target_price)}</div>
                {potentialReturn && (
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                    +{potentialReturn.toFixed(1)}% upside
                  </div>
                )}
              </div>
            )}
            
            {investmentTip.risk_level && (
              <div className="bg-gradient-secondary rounded-xl p-4 border border-border/20">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Risk Level</span>
                </div>
                <Badge className={`${getRiskLevelColor(investmentTip.risk_level)} text-sm font-semibold`}>
                  {investmentTip.risk_level.toUpperCase()}
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Investment Details */}
        {investmentTip.investment_horizon && (
          <div className="flex items-center gap-6 p-4 bg-gradient-secondary rounded-xl border border-border/20">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Investment Horizon:</span>
              <Badge variant="outline" className="font-medium">
                {investmentTip.investment_horizon.replace('-', ' ').toUpperCase()}
              </Badge>
            </div>
          </div>
        )}

        {/* TLDR Section */}
        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-bold text-primary uppercase tracking-wide">
              💰 TLDR
            </span>
          </div>
          <p className="text-foreground/90 text-sm font-medium leading-relaxed">
            {generateTLDR((() => {
              let content = investmentTip.content;
              // Parse JSON content if it's a string containing JSON
              try {
                const parsed = JSON.parse(content);
                content = parsed.content || content;
              } catch {
                // If not JSON, use as is
              }
              return content;
            })())}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border/20">
          <Button asChild variant="default" className="flex-1">
            <Link to={`/investment-tips/${investmentTip.id}`}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Read Full Analysis
            </Link>
          </Button>
          {investmentTip.video_url && (
            <Button asChild variant="outline" className="flex-1">
              <a href={investmentTip.video_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Watch Video Analysis
              </a>
            </Button>
          )}
        </div>

        {/* Disclaimer */}
        <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3 border border-border/20">
          <strong>Disclaimer:</strong> This analysis is for educational purposes only and should not be considered as financial advice. 
          Always conduct your own research and consult with a qualified financial advisor before making investment decisions.
        </div>
      </CardContent>
    </Card>
  );
};